import { Link } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '../../hooks/redux'
import { logout } from '../../store/slices/authSlice'
import { useCompatRouter } from '../../hooks/useCompatRouter'
import { signOutPortalUser } from '../../services/guestPortalSupabase'
import resortLogo from '../../assets/resort-logo.jpg'

interface LayoutProps {
  children: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const router = useCompatRouter()
  const { isAuthenticated, user } = useAppSelector((state) => state.auth)
  const dispatch = useAppDispatch()

  const isActive = (path: string) => {
    const currentPath = router.asPath.split('?')[0]
    return currentPath === path
  }

  const handleLogout = async () => {
    await signOutPortalUser().catch(() => undefined)
    dispatch(logout())
    void router.push('/booking-system/login')
  }

  return (
    <div className="min-h-screen">
      <header className="fixed top-0 w-full z-50 bg-white/86 backdrop-blur-xl border-b border-slate-200/80 shadow-[0_18px_40px_rgba(15,23,42,0.10)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/booking-system" className="flex items-center space-x-3">
                <div className="w-16 h-12 rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm">
                  <img src={resortLogo} alt="Marina Glen Holiday Resort Logo" className="w-full h-full object-contain bg-white p-1" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-950">Marina Glen</h1>
                  <p className="text-xs tracking-[0.18em] uppercase text-slate-500">Holiday Resort</p>
                </div>
              </Link>
            </div>
            
            <nav className="hidden md:flex space-x-2">
              <Link 
                to="/booking-system" 
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                  isActive('/booking-system') 
                    ? 'bg-slate-950 text-white shadow-sm' 
                    : 'text-slate-700 hover:text-slate-950 hover:bg-slate-100'
                }`}
              >
                Home
              </Link>
              <Link 
                to="/booking-system/rooms" 
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                  isActive('/booking-system/rooms') 
                    ? 'bg-slate-950 text-white shadow-sm' 
                    : 'text-slate-700 hover:text-slate-950 hover:bg-slate-100'
                }`}
              >
                Units
              </Link>
              <Link 
                to="/booking-system/booking" 
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                  isActive('/booking-system/booking') 
                    ? 'bg-slate-950 text-white shadow-sm' 
                    : 'text-slate-700 hover:text-slate-950 hover:bg-slate-100'
                }`}
              >
                Book Now
              </Link>
              <Link 
                to="/booking-system/dashboard" 
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                  isActive('/booking-system/dashboard') 
                    ? 'bg-slate-950 text-white shadow-sm' 
                    : 'text-slate-700 hover:text-slate-950 hover:bg-slate-100'
                }`}
              >
                Dashboard
              </Link>
              {isAuthenticated && user?.role === 'admin' && (
                <Link 
                  to="/booking-system/admin" 
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                    isActive('/booking-system/admin') 
                      ? 'bg-slate-950 text-white shadow-sm' 
                      : 'text-slate-700 hover:text-slate-950 hover:bg-slate-100'
                  }`}
                >
                  Admin
                </Link>
              )}
            </nav>

            <div className="flex items-center space-x-3">
              {isAuthenticated ? (
                <div className="flex items-center space-x-3">
                  <span className="text-slate-600 text-sm">
                    Welcome, {user?.firstName} {user?.role === 'admin' && '(Admin)'}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="text-slate-700 hover:text-slate-950 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 hover:bg-slate-100"
                  >
                    Sign Out
                  </button>
                </div>
              ) : (
                <Link 
                  to="/booking-system/login" 
                  className="text-slate-700 hover:text-slate-950 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 hover:bg-slate-100"
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>
      
      <main className="pt-20">{children}</main>
      
      <footer className="bg-slate-950/82 backdrop-blur-xl text-white border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-9 rounded-lg overflow-hidden bg-white/10">
                  <img src={resortLogo} alt="Marina Glen Holiday Resort Logo" className="w-full h-full object-contain bg-white p-1 rounded" />
                </div>
                <h3 className="text-xl font-bold">Marina Glen Holiday Resort</h3>
              </div>
              <p className="text-secondary-300 mb-4 max-w-md">
                Experience luxury and comfort at our world-class holiday resort. 
                Professional booking management system for seamless reservations with breathtaking ocean views.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4 text-primary-300">Quick Links</h4>
              <ul className="space-y-2 text-secondary-300">
                <li><Link to="/booking-system/rooms" className="hover:text-white transition-colors hover:text-primary-300">Units</Link></li>
                <li><Link to="/booking-system/booking" className="hover:text-white transition-colors hover:text-primary-300">Book Now</Link></li>
                <li><Link to="/booking-system/dashboard" className="hover:text-white transition-colors hover:text-primary-300">Dashboard</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4 text-primary-300">Contact</h4>
              <ul className="space-y-2 text-secondary-300">
                <li>📞 0393130200</li>
                <li>📧 bookings@marinaglen.co.za</li>
                <li>🏖️ 17 Mars Road, Marina Beach, KZN</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-secondary-800 mt-8 pt-8 text-center text-secondary-400">
            <p>&copy; 2025 Marina Glen Holiday Resort. All rights reserved. 🌊</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Layout